'use strict';
const trackingUtils = require('od-modules/utils/tracking.utils');
const filterParser = require('../filters/utils/parser');
const computeFilterLegs = require('../filters/utils/compute_filter_legs');
const POST_DATA_REQUEST_SETTINGS = {
    type: 'POST',
    contentType: 'application/json',
    dataType: 'json',
};
const isRequestProp = Odigeo._.ary(Odigeo._.partial(Odigeo._.endsWith, _, 'FilterModel'), 1);
const filterModelProps = function(predicate, model) {
    return Odigeo._.pick(model, Odigeo._.filter(Odigeo._.keys(model), predicate));
};
const isValidIndex = function(index) {
    return index && typeof index === 'string' && index.indexOf('-') !== -1;
};
const filterRequestProps = Odigeo._.partial(filterModelProps, isRequestProp);
const filterStatusProps = Odigeo._.partial(filterModelProps, Odigeo._.negate(isRequestProp));
const tripTypeUtils = require('od-modules/utils/trip_type');
const sortTypes = require('../../modules/utils/sort_types');

const normalizeRequestModels = function(requestModels, numFlights, tripType) {
    var models = requestModels;
    var firstRequestModel = requestModels[0];
    var length = numFlights;
    if (tripType === 'roundTrip' && numFlights === 1) {
        models = [firstRequestModel, firstRequestModel];
        length = 2;
    } else if (tripType === 'oneWayTrip') {
        length = 1;
    }
    return models.slice(0, length);
};
/**
 * Creates an object with keys that are requestTypes ('origins', 'destinations', 'stopovers', ...) with array values
 * that are $.Deferred<RequestModel>s
 * @param {Array<String>} filterRequestTypes  array of request types
 * @param {Number} numFlights number that corresponds to number of RequestModels requested for each requestType
 * @return {Object<String, Array<$.Deferred<RequestModel>>>}
 *
 * Example:
 * filterRequests: ['origins', 'destinations']
 * numFlights: 2
 *
 * returns {
 *     origins: [<$.Deferred()>, <$.Deferred()>],
 *     destinations: [<$.Deferred()>, <$.Deferred()>]
 * }
 */
const createDeferredRequestModels = function(filterRequestTypes, numFlights) {
    return Odigeo._.reduce(filterRequestTypes, function(memo, filterRequestType) {
        memo[filterRequestType] = Odigeo._.map(Odigeo._.range(numFlights), function() {
            return $.Deferred();
        });
        return memo;
    }, {});
};
/**
 * Waits for resolving all deferred objects returned by `createDeferredRequestModels`.
 * It returns a promise with all $.Deferred object resolved
 * @param {Number} numFlights
 * @param {String} tripType
 * @return {Promise}
 *
 * Example:
 * numFlights: 2
 * tripType: 'multipleTrip'
 *
 * returns Promise<{
 *     originsFilterModel: [<RequestModel>, <RequestModel>],
 *     destinationsFilterModel: [<RequestModel>, <RequestModel>]
 * }
 */
const waitForReceivingRequestModels = function(numFlights, tripType) {
    var model = this;
    var deferredRequestModels = model.deferredRequestModels;
    var filterRequestTypes = Odigeo._.keys(deferredRequestModels);
    var filterModelsPromises = Odigeo._.map(deferredRequestModels, function(filterModelsPromise) {
        return $.when.apply($, filterModelsPromise).then(function() {
            return Odigeo._.toArray(arguments);
        });
    });

    return $.when.apply($, filterModelsPromises)
        .then(function() {
            var filterRequestModelsArray = Odigeo._.toArray(arguments);
            return Odigeo._.reduce(filterRequestModelsArray, function(memo, filterRequestModels, index) {
                var requestType = filterRequestTypes[index];
                var modelRequestProp = (model.requestTypeToModelProp[requestType] || requestType) + 'FilterModel';
                memo[modelRequestProp] = normalizeRequestModels(filterRequestModels, numFlights, tripType);
                return memo;
            }, {});
        });
};
const requestFilterInfo = function(eventName, filterRequestTypes, filterName) {
    var widget = this.widget;
    var filterType = filterName.replace('_filter', '');
    var numFlights = computeFilterLegs(filterType, widget.tripType, widget.numLegs);
    this.deferredRequestModels = createDeferredRequestModels(filterRequestTypes, numFlights);

    var prom = waitForReceivingRequestModels.call(this, numFlights, widget.tripType);
    Odigeo._.each(filterRequestTypes, function(requestType) {
        for (var i = 0; i < numFlights; i++) {
            const idx = (widget.options.isFiltersLayer && requestType === 'stopovers') ? `stopover-${i}` : i;
            Odigeo.trigger(widget.id + ':' + eventName, requestType, filterName, idx);
        }
    });
    return prom;
};

let FILTER_URL = {
    SET: 'filterset',
    SORT: 'filter/sort',
    RESET: 'filter/reset',
    FILTER: 'filter',
    RESET_AND_SORT: 'filter/resetandsort',
    FILTER_AND_SORT: 'filter/filterandsort',
    RESET_AND_FILTER: 'filter/resetandfilter',
};

module.exports = {
    dataURL: 'filter',
    dataApplySearchFilters: true,
    mode: '',
    data: {},
    isEmptyData: false,
    matrixItineraryId: '',
    isRestoringStatus: false,
    filtersMetadataToRestore: {timeFilterModel: []},
    filtersMetadataTemporal: {timeFilterModel: []},
    dependencies: {
        airports: [{
            name: 'stopovers',
            getInfo: 'getStopoverDependenciesForAirport',
        }],
    },
    config: {
        MODES: {
            init: {
                resultSchema: 'getResetAndSortModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.RESET_AND_SORT,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'resetRequestSuccessHandler',
            },
            load: {
                urlFormatter: 'widgetPartUrlFormatter',
                widgetProp: 'name',
                dataURL: FILTER_URL.SET,
                successHandler: 'loadRequestSuccessHandler',
            },
            filter: {
                resultSchema: 'getFilterModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.FILTER,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'filterRequestSuccessHandler',
            },
            resetandfilter: {
                resultSchema: 'getFilterModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.RESET_AND_FILTER,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'filterRequestSuccessHandler',
            },
            reset: {
                resultSchema: 'getResetRequestModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.RESET,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'resetRequestSuccessHandler',
            },
            filterandsort: {
                resultSchema: 'getFilterAndSortModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.FILTER_AND_SORT,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'filterRequestSuccessHandler',
                resetMode: 'filter',
            },
            resetandsort: {
                resultSchema: 'getResetAndSortModel',
                urlFormatter: 'serviceUrlFormatter',
                dataURL: FILTER_URL.RESET_AND_SORT,
                dataRequestSettings: POST_DATA_REQUEST_SETTINGS,
                successHandler: 'resetRequestSuccessHandler',
            },
        },
    },
    requestTypeToModelProp: {
        stopduration: 'stopDuration',
        airlines: 'airline',
    },
    filtersApplied: {},
    defaults: {
        isMembershipFare: true,
    },
    init() {
        const tests = this.widget.options.tests;
        const isDynpack = Odigeo.Utils.isRequestApp('dp');
        this.currentSorting = !isDynpack && tests.sortingTabsEnabled &&
        (tests.sortingTabsPartition === 2) ? sortTypes.byRecommended.value : sortTypes.byPrice.value;
    },
    onFiltersUpdated(idx, filter, isFiltered) {
        var applied = this.filtersApplied[filter] || [];

        if (!isFiltered) {
            applied = applied.filter(function(n) {
                return n !== idx;
            });
        } else if (applied.indexOf(idx) === -1) {
            applied.push(idx);
        }

        if (applied.length > 0) {
            this.filtersApplied[filter] = applied;
        } else {
            delete this.filtersApplied[filter];
        }

        this.setFiltersApplied();
    },
    getDataRequestSettings() {
        var dataRequestSettings = {
            type: 'GET',
            data: this._getResultJSONData(),
            cache: false,
            success: Odigeo._.bind(this.requestSuccessHandler, this),
            error: Odigeo._.bind(this.requestErrorHandler, this),
        };
        return Odigeo._.extend(dataRequestSettings, this.config.MODES[this.mode].dataRequestSettings);
    },
    checkNoAirlinesSelected() {
        return requestFilterInfo.call(this, 'isZeroApplied', ['airlines'], 'airlines_filter')
            .then(Odigeo._.flow(Odigeo._.property('airlineFilterModel'), Odigeo._.some));
    },
    checkNoAirportsSelected() {
        var requestTypes = ['origins', 'destinations'];
        return requestFilterInfo.call(this, 'isZeroApplied', requestTypes, 'airports_filter')
            .then(function(models) {
                return Odigeo._.some(requestTypes, function(requestType) {
                    return Odigeo._.some(models[requestType + 'FilterModel']);
                });
            });
    },
    getStopoverDependenciesForAirport(idx) {
        var $stopovers = this.widget.view.$cachedSelectors['filter_stops' + idx + '_stopover_input'];
        var $selectedStopovers = $stopovers.filter(':checked');
        return {
            isStopoverAllButtonDisabled: $stopovers.length === $selectedStopovers.length,
        };
    },

    _getResultJSONData() {
        const resultSchemaMethodName = this.config.MODES[this.mode].resultSchema;
        return Odigeo._.isString(resultSchemaMethodName) ? JSON.stringify(this[resultSchemaMethodName]()) : {};
    },
    url() {
        var widget = this.widget;
        var modeOptions = this.config.MODES[this.mode];
        var Settings = Odigeo.Settings;
        var url;
        if (Odigeo._.isObject(modeOptions)) {
            var widgetProp = modeOptions.widgetProp;
            var params = [];
            if (Odigeo._.isString(widgetProp)) {
                params.push(widget[widgetProp]);
            }
            params.push(modeOptions.dataURL);
            url = Settings.getProperty(modeOptions.urlFormatter).apply(Settings, params);
        } else {
            url = Settings.getProperty('serviceUrlFormatter')(this.dataURL);
        }
        return url;
    },
    checkeventToTrigger(eventToTrigger, msg, params) {
        if (eventToTrigger === 'zeroFilteredResults') {
            trackingUtils.trackEvent('results_list', 'filtering_no_results_error_airportcombination_onload');
            if (Odigeo.AB.isNewNoResultsModal) {
                this.excetuteHandlersToShowNoResultsByPreFilter(msg);
            } else {
                Odigeo.trigger(`${this.widget.id}:zeroFilteredResults`, params);
            }
        } else {
            Odigeo.trigger(`${this.widget.id}:resetFilters`, params);
        }
    },
    loadRequestSuccessHandler(msg, searchParameters) {
        var STATUS_WHEN_EXPIRED_SESSION = '';
        var widget = this.widget;
        var resultsCount;
        if (msg === STATUS_WHEN_EXPIRED_SESSION) {
            Odigeo.trigger(this.widget.id + ':sessionExpiredError');
            return;
        }

        widget.tripType = searchParameters.type;
        widget.numLegs = widget.numFlights = searchParameters.searchSegments.length;
        if (tripTypeUtils.isRoundTrip(widget.tripType)) {
            widget.numFlights = widget.options.numFlightsForRoundTrip || 2;
        }
        resultsCount = Odigeo.Storage.get('results_count');

        if (resultsCount > 0) {
            widget.view.loadFilters(msg);
            this.mode = 'init';
            this.dataApplySearchFilters = Odigeo._.has(searchParameters, 'internalSearch') && searchParameters.internalSearch;
            this.refresh();
        } else {
            Odigeo.trigger(this.widget.id + ':zeroResults');
        }
    },

    sortRequestSuccessHandler(msg, searchParameters, params) {
        this.resetRequestSuccessHandler(msg, searchParameters, params);
        if (Odigeo.AB.isFloatingFiltersEnabled && typeof msg.numResults === 'number'
            && typeof msg.numFilteredResults === 'number') {
            Odigeo.trigger('remove_filters:updateAmountOfFlightsResults', msg.numFilteredResults, msg.numResults);
        }
        this.setFiltersApplied();
    },

    excetuteHandlersToShowNoResultsByPreFilter(msg) {
        return new Promise((resolve) => {
            resolve(Odigeo.trigger(`${this.widget.id}:openNoResultsWhenFilteringByAirport`, msg));
        }).then(() => {
            Odigeo.trigger(`results-manager:reset-filters`);
        });
    },

    resetRequestSuccessHandler: function(msg, searchParameters, params) {
        if (!Odigeo._.isObject(msg)) {
            this.mode = 'filter';
            return;
        }
        params['isFiltering'] = false;
        params['numResults'] = msg['numResults'];
        params['numFilteredResults'] = msg['numFilteredResults'];
        params['cheapestPriceNotMatchedFilter'] = msg['cheapestPriceNotMatchedFilter'];
        params['numPreFilteredResults'] = msg['numPreFilteredResults'];

        this.set('numFilteredResults', msg.numFilteredResults);
        const eventToTrigger = msg.numFilteredResults === 0 ? 'zeroFilteredResults' : 'resetFilters';

        if (Odigeo.Session.get('isFirstBatch')) {
            Odigeo.trigger(`${this.widget.id}:${eventToTrigger}`, params);
        } else {
            const widget = this.widget;
            const tripOptions = Odigeo._.pick(widget, ['numLegs', 'tripType']);
            this.clearRequest({silent: true});

            widget.view.initializeFilters(filterParser.parserResetMetadata(msg, tripOptions));

            if (msg.numResults > 0) {
                widget.view.updateFilters(filterParser.parseFilterMetadata(msg, tripOptions));

                let $elementsToHideWhileFiltering = null;
                const prefilterApplicable = this.mode === 'init' && searchParameters.directFlightsOnly;
                const minStops = msg.filtersMetadataAll[0].minStops;
                if (prefilterApplicable && minStops <= 0) {
                    Odigeo.trigger(`${this.widget.id}:allowToRefreshResults`, false);
                }

                this.checkeventToTrigger(eventToTrigger, msg, params);
                Odigeo.trigger(`${this.widget.id}:deleteNonStopFlightsMessage`);
                this.mode = 'filter';

                if (prefilterApplicable) {
                    if (minStops <= 0) {
                        $elementsToHideWhileFiltering = $('.home_resultpage_results, .result-page-modsky');
                        $elementsToHideWhileFiltering.addClass('hidden');
                        this.widget.doCheckNonStopsFilter(true);
                        $elementsToHideWhileFiltering.removeClass('hidden');
                    } else {
                        Odigeo.trigger(`${this.widget.id}:showNonStopFlightsMessage`);
                        trackingUtils.trackEvent('results_list', 'direct_flights_no_results');
                    }
                }
                this.dataApplySearchFilters = false;
            } else {
                Odigeo.trigger(this.widget.id + ':zeroResults');
                this.mode = 'filter';
            }
        }
    },

    filterRequestSuccessHandler(msg, searchParameters, params) {
        Odigeo.trigger(this.widget.id + ':allowToRefreshResults', true);
        var widget = this.widget;
        var modeOptions = widget.model.config.MODES[this.mode];
        var updateOnNoneSelected = widget.options.updateOnNoneSelected;
        var showFilterResult;
        var resetMode = modeOptions.resetMode;
        var attributes = this.attributes;
        if (Odigeo._.isString(resetMode)) {
            this.mode = resetMode;
        }

        var data = filterParser.parseFilterMetadata(msg, Odigeo._.pick(widget, ['numLegs', 'tripType']));
        widget.view.updateFilters(data, this.isRestoringStatus);

        params = Odigeo._.extend({isFiltering: true, filtersApplied: Odigeo._.keys(attributes)}, msg);

        Odigeo.trigger(widget.id + ':filterApplied', widget.activeFilter, this.mode, params);
        updateOnNoneSelected = Odigeo._.isUndefined(updateOnNoneSelected) ? true : updateOnNoneSelected;

        let filterModelsToIntersect = [
            'destinationsFilterModel',
            'originsFilterModel',
            'airlineFilterModel',
            'stopoversFilterModel',
        ];

        if (this.widget.options.isFiltersLayer) {
            filterModelsToIntersect.push('timeFilterModel', 'stopsFilterModel', 'transportTypeFilterModel');
        }

        showFilterResult = Odigeo._.intersection(params.filtersApplied, filterModelsToIntersect).length === 0;
        this.set('numFilteredResults', msg.numFilteredResults);
        if ((updateOnNoneSelected || showFilterResult) && msg.numFilteredResults === 0) {
            Odigeo.trigger(widget.id + ':zeroFilteredResults');
        }
        if (msg['numFilteredResults'] === 0) {
            Odigeo.trigger(widget.id + ':stopFilterFlightsTime');
        }

        this.setFiltersApplied();
    },

    setFiltersApplied() {
        var filtersApplied = {
            airports: Odigeo._.has(this.filtersApplied, 'airports_filter'),
            stops: Odigeo._.has(this.filtersApplied, 'stopover_duration_filter') || Odigeo._.has(this.filtersApplied, 'stopover_quantity_filter'),
            airlines: Odigeo._.has(this.filtersApplied, 'airlines_filter'),
            times: Odigeo._.has(this.filtersApplied, 'time_filter') || Odigeo._.has(this.filtersApplied, 'time_ranges_filter'),
        };

        Odigeo._.each(filtersApplied, function(isFiltered, tab) {
            Odigeo.trigger(this.widget.id + ':toogleAppliedSign', tab, isFiltered);
        }, this);

        if (Odigeo.AB.isFloatingFiltersEnabled) {
            Odigeo.trigger('filters-manager:getAllFiltersApplied', filtersApplied);
            Odigeo.trigger('resultspagefiltertabs:setFiltersApplied', Odigeo._.size(this.filtersApplied));
        } else {
            Odigeo.trigger('remove_filters:setFiltersApplied', Odigeo._.size(this.filtersApplied));
        }
    },

    receiveFilterRequest(requestType, filterName) {
        requestFilterInfo.call(this, 'requestModel', requestType, filterName)
            .then(Odigeo._.bind(this.onReceiveFilterModels, this));
    },

    waitForReceivingAirportModels(tripType) {
        this.deferredRequestModels = {
            origins: [$.Deferred()],
            destinations: [$.Deferred()],
        };
        return waitForReceivingRequestModels.call(this, 1, tripType);
    },

    requestSuccessHandler(msg) {
        const widget = this.widget;
        const modeOptions = widget.model.config.MODES[this.mode];
        let params = {};

        if (Odigeo._.isObject(msg)) {
            msg = this.mapFlightMetaData(msg);
            if (Odigeo.AB.isFloatingFiltersEnabled && typeof msg.numResults === 'number'
                && typeof msg.numFilteredResults === 'number') {
                Odigeo.trigger('remove_filters:updateAmountOfFlightsResults', msg.numFilteredResults, msg.numResults);
            }
        }

        if (msg.errors) {
            if (msg.errors[0].code === 'SESSION_EXPIRED_ERROR') {
                Odigeo.trigger(this.widget.id + ':sessionExpiredError');
            } else if (msg.errors[0].code !== 'SEARCH_EXPIRED_ERROR') {
                Odigeo.trigger(this.widget.id + ':brokenFlowError');
            }
        } else {
            let searchParameters = Odigeo.Session.get('searchParameters');
            if (Odigeo._.isNull(searchParameters)) {
                return;
            }
            searchParameters = JSON.parse(searchParameters);
            widget.options = Odigeo.Utils.parseOptions(widget.options);

            this[modeOptions.successHandler](msg, searchParameters, params);
        }
    },
    mapFlightMetaData(metadata) {
        var mapObj = Odigeo._.omit(metadata, 'flightSearchResultsMetadata', 'flightFiltersMetadataAll');

        mapObj.filtersMetadataAll = metadata.flightFiltersMetadataAll;

        return Odigeo._.extend(mapObj, metadata.flightSearchResultsMetadata);
    },
    requestErrorHandler(xhr) {
        if (xhr.status === 404) {
            Odigeo.trigger(this.widget.id + ':error404');
        } else if (this.mode === 'load') {
            Odigeo.trigger(this.widget.id + ':sessionExpiredError');
        } else {
            Odigeo.trigger(this.widget.id + ':error500');
        }
    },
    getResetRequestModel() {
        return {
            hasToApplySearchFilters: this.dataApplySearchFilters,
        };
    },
    getFilterModel() {
        return filterRequestProps(this.toJSON());
    },
    getFilterAndSortModel() {
        var requestData = {};
        requestData.filterRequest = this.getFilterModel();
        requestData.sortRequest = {
            sortType: this.currentSorting,
        };
        return requestData;
    },
    getSortModel() {
        return {
            sortType: this.currentSorting,
            hasToApplySearchFilters: this.dataApplySearchFilters,
        };
    },
    getResetAndSortModel() {
        var requestData = {};
        requestData.resetRequest = {
            hasToApplySearchFilters: this.dataApplySearchFilters,
        };
        if (this.widget.options.tests.sortingTabsEnabled && tripTypeUtils.isMultipleDestinations(this.widget.tripType)) {
            this.currentSorting = sortTypes.byPrice.value;
        }
        requestData.sortRequest = {
            sortType: this.currentSorting,
        };
        return requestData;
    },
    /**
     * do a request based on filter models related to fields list
     * @param {Array} fields
     * @deprecated Do not add new requests (switch-cases) in this method.
     * This method is deprecated and now, each filter widget recovers its model.
     * See modelClass#requestModels() method.
     */
    filterRequest(fields) {
        var filterRequestData = {};

        for (var i = 0; i < fields.length; i++) {
            var field = fields[i];
            switch (field) {
                case 'trains':
                    filterRequestData.trainsFilterModel = this.getTrainsModel();
                    break;
                case 'stopovers':
                    filterRequestData.stopoversFilterModel = this.getStopoversModel();
                    break;
                case 'fastestflightduration':
                    filterRequestData.flightDurationFilterModel = this.getFlightDurationModel();
                    break;
                case 'flightduration':
                    filterRequestData.flightDurationFilterModel = this.getFlightDurationModel();
                    break;
                case 'differentCarriers':
                    filterRequestData.differentCarriersFilterModel = this.getDifferentCarriers();
                    break;
                case 'itinerariesKey':
                    filterRequestData.itineraryKeyFilterModel = this.getItinerariesKey();
                    break;
            }
        }
        this.clearRequest({silent: true});
        this.set(filterRequestData);
        this.refresh();
    },
    filterTransportTypeRequest(selectedItems) {
        const filterRequestData = {transportTypeFilterModel: this.getTransportTypeModel(selectedItems)};
        this.mode = 'filterandsort';
        this.clearRequest({silent: true});
        this.set(filterRequestData);
        this.refresh();
    },
    getTransportTypeModel(selectedItems) {
        const getFilterModel = function() {
            return {selectedItems};
        };
        return this._getFilters(getFilterModel);
    },
    clearRequest(options) {
        var filterModel = this.toJSON();
        this.clear(options);
        this.set(filterStatusProps(filterModel), options);
    },
    getItinerariesKey() {
        var itinerariesKey = [];

        if (!Odigeo._.isEmpty(this.matrixItineraryId)) {
            itinerariesKey.push({itinerariesKey: [this.matrixItineraryId]});
            this.matrixItineraryId = '';
        }
        return itinerariesKey;
    },
    getDifferentCarriers() {
        return [
            {checked: true},
        ];
    },
    // -------------- model getters --------------
    getTrainsModel() {
        var widget = this.widget;
        var getFilterModel = function(i) {
            var filterModel = {};
            filterModel.include = widget.view.$cachedSelectors['filter_airlines' + i + '_train'].is(':checked');
            return filterModel;
        };
        return this._getFilters(getFilterModel);
    },
    getStopoversModel() {
        return this._getFilterModel('filter_stops', '_stopover_input');
    },
    getFlightDurationModel() {
        var widget = this.widget;
        var getFilterModel = function(i) {
            var filterModel = {};
            filterModel.selectedDuration = widget.view.$widget.find('#fDurationSelectedSlider' + i).attr('to');
            return filterModel;
        };
        return this._getFilters(getFilterModel);
    },
    _getFilterModel(filterType, inputName) {
        var widget = this.widget;
        var getFilterModel = function(i) {
            var filterModel = {};
            filterModel.selectedItems = [];
            widget.view.$cachedSelectors[filterType + i + inputName].filter(':checked').each(function() {
                filterModel.selectedItems.push($(this).val());
            });
            return filterModel;
        };
        return this._getFilters(getFilterModel);
    },
    _getFilters(getFilterModel) {
        var widget = this.widget;
        var all = [];
        var i;

        if (widget.tripType === 'multipleTrip') {
            for (i = 0; i < widget.numFlights; i++) {
                all.push(getFilterModel(i, widget));
            }
        } else if (tripTypeUtils.isRoundTrip(widget.tripType)) {
            all.push(getFilterModel(0));
            var duplicateFilter = widget.numFlights === 1;
            if (duplicateFilter) {
                all.push(all[0]);
            } else {
                all.push(getFilterModel(1));
            }
        } else {
            all.push(getFilterModel(0));
        }
        return all;
    },
    onReceiveFilterModels(filterRequestData, isRestoringStatus) {
        this.isRestoringStatus = isRestoringStatus;
        this.clearRequest({silent: true});
        this.set(filterRequestData);
        if (this.widget.options.tests.sortingTabsEnabled) {
            this.mode = 'filterandsort';
        }
        this.refresh();
    },
    receiveFilterInfo(info, requestType, index) {
        let idx = index;
        if (this.widget.options.isFiltersLayer &&
            isValidIndex(index)) {
            const splitIndex = index.split('-');
            idx = Number(splitIndex[splitIndex.length - 1]);
        }
        this.deferredRequestModels[requestType][idx].resolve(info);
    },
    resetFiltersMetadataTemporal() {
        this.filtersMetadataTemporal = {timeFilterModel: []};
    },
    resetFiltersMetadataToRestore() {
        this.filtersMetadataToRestore = {timeFilterModel: []};
    },
    updateFiltersMetadataTemporal(filterInfo, requestType, index) {
        this.filtersMetadataTemporal.timeFilterModel[index] = Odigeo._.cloneDeep(filterInfo);
    },
    storeFiltersMetadataStatus() {
        this.filtersMetadataToRestore = Odigeo._.cloneDeep(this.filtersMetadataTemporal);
        this.filtersMetadataTemporal = {timeFilterModel: []};
    },
};
